import React from 'react';
import { CheckMark } from 'src/assets/images';
import { Colors } from 'src/common';

import styled from 'styled-components';

enum BallState {
  COMPLETE = 0,
  ACTIVE = 1,
  DEFAULT = 2,
}

const StepperContainer = styled.section.attrs({ className: 'stepper-container' })`
  display: flex;
  flex-direction: column;
  width: 40rem;
  margin: auto;
  padding: 1.5rem 0;
`;

const ListContainer = styled.section.attrs({ className: 'stepper-list-container' })`
  width: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
  text-align: center;
`;

const Progress = styled.hr<{ $value: number }>`
  flex-grow: 1;
  height: 2px;
  margin: 0;
  border: none;

  ${({ $value }) => `
		background: linear-gradient(to right, ${Colors.Red50} ${$value}%, ${Colors.Grey600} 0%);
	`}
`;

const BallWrapper = styled.div`
  width: 10px;
  height: 35px;
  position: relative;
  display: flex;
  justify-content: center;
  align-items: center;
`;

interface BallStepProps {
  $active?: boolean;
  $completed?: boolean;
  $default?: boolean;
  $size?: string;
}

const BallStep = styled.div.attrs((props: BallStepProps) => ({
  className: `stepper-ball-step stepper-ball-step-${
    props.$default ? 'default' : props.$completed ? 'completed' : 'active'
  }`,
}))<BallStepProps>`
  display: flex;
  justify-content: center;
  align-items: center;
  text-align: center;
  position: absolute;

  border-radius: 50%;
  border: 5px solid ${Colors.Black};

  ${({ $active }) =>
    $active &&
    `
		color: ${Colors.Red50};
		background-color: ${Colors.White};
	`}

  ${({ $completed }) =>
    $completed &&
    `
		background-color: ${Colors.Red50};
	`}

	${({ $default }) =>
    $default &&
    `
		background-color: ${Colors.Grey100};
	`}

	${({ $size }) => `
		width: ${$size || '30px'};
		height: ${$size || '30px'};
	`}
`;

const Tick = styled.img`
  height: 10px;
`;

const Ball = ({ state, index }: { state: BallState; step: Step; index: number }) => {
  if (state === BallState.COMPLETE) {
    return (
      <BallStep $completed>
        <Tick src={CheckMark} alt="tick" />
      </BallStep>
    );
  }

  if (state === BallState.ACTIVE) {
    return <BallStep $active>{index}</BallStep>;
  }

  return <BallStep $default $size="9px" />;
};

type Step = {
  label: string;
  substeps: number;
};

export interface StepperProps {
  steps: Step[];
  currentStep: number;
  currentSubStep?: number;
  onBackButton?: () => void;
  hideProgress?: boolean;
  hideBackButton?: boolean;
}

const Label = styled.div.attrs((props: { $state: BallState }) => ({
  className: `stepper-label ${props.$state === BallState.ACTIVE ? 'stepper-label-active' : ''}`,
}))<{ $state: BallState }>`
  max-width: 10px;
  text-align: center;
  display: flex;
  justify-content: center;
  align-items: center;
  white-space: nowrap;
  margin-top: 1rem;
  font-size: 14px;

  ${({ $state }) =>
    $state === BallState.ACTIVE &&
    `
		color: ${Colors.Red};
	`}

  ${({ $state }) =>
    $state === BallState.COMPLETE &&
    `
		color: ${Colors.Grey300};
	`}

	${({ $state }) =>
    $state === BallState.DEFAULT &&
    `
		color: ${Colors.Grey400};
	`}
`;

const Spacer = styled.div`
  flex-grow: 1;
  background: transparent;
  height: 0;
  pointer-events: none;
`;

/**
 * It is important to note that this component does not work on mobile due to design.
 * For mobile, use the `Progress` component.
 */
export const Stepper: React.FC<StepperProps> = ({
  steps,
  currentStep,
  hideProgress,
  currentSubStep = 1,
}) => {
  const getStepState = React.useCallback(
    (step: Step, index: number): BallState => {
      switch (true) {
        case index < currentStep:
          return BallState.COMPLETE;

        case index === currentStep:
          return BallState.ACTIVE;

        case index === currentStep && currentSubStep / step.substeps >= 1:
          return BallState.COMPLETE;

        case index > currentStep:
          return BallState.DEFAULT;
      }

      return BallState.DEFAULT;
    },
    [currentStep, currentSubStep],
  );

  const getProgress = React.useCallback(
    (step: Step, index: number): number => {
      if (index < currentStep) {
        return 100;
      }

      if (index === currentStep) {
        return Math.round(50 + (currentSubStep / step.substeps) * 50);
      }

      return 0;
    },
    [currentStep, currentSubStep],
  );

  return (
    <StepperContainer>
      {!hideProgress && (
        <ListContainer>
          {steps.map((step, index, arr) => (
            <React.Fragment key={`${step.label}-${index}-step`}>
              <BallWrapper>
                <Ball state={getStepState(step, index)} index={index + 1} step={step} />
              </BallWrapper>
              {index !== arr.length - 1 && <Progress $value={getProgress(step, index)} />}
            </React.Fragment>
          ))}
        </ListContainer>
      )}

      {!hideProgress && (
        <ListContainer>
          {steps.map((step, index, arr) => (
            <React.Fragment key={`${step.label}-${index}-label`}>
              <Label $state={getStepState(step, index)}>{step.label}</Label>
              {index !== arr.length - 1 && <Spacer />}
            </React.Fragment>
          ))}
        </ListContainer>
      )}
    </StepperContainer>
  );
};
